import { useEffect, useState } from 'react';
import { Formik, FormikHelpers } from 'formik';
import { ButtonProps, BoxProps, Flex, Box } from '@chakra-ui/react';
import { useValidateEmail, useVariable, useFetchCountries, useFetchCountryStates, useToast } from 'hooks';
import {
  validateRequired,
  validateConfirm,
  validatePassword,
  validateEmail,
  validatePostalCode,
} from 'utils/validation';

import {
  errorMessageConfirmPassword,
  errorMessageEmailInvalid,
  errorMessagePasswordShort,
  errorMessagePasswordStrength,
  errorMessagePhoneNumberInvalid,
  errorMessageRecaptchaFailed,
  errorMessageRequired,
  errorMessageZipCode,
} from 'constants/validation';

import { AccountStatus, accountStatuses } from 'constants/accountStatuses';
import {
  FormControl,
  FormLabel,
  Recaptcha,
  Input,
  SimpleGrid,
  Heading,
  Button,
  FormSelect,
  Alert,
  AlertTitle,
  AlertDescription,
  AlertIcon,
  FormSelectProps,
  Spinner,
} from 'components/UI';
import { checkIfError, validateForm } from 'utils/helpers';
import { ThemeContext } from 'contexts/themeContext';
import { ISettings } from 'types';
import { getCountryRequiredFields } from 'utils/countries';
import { otherCountry } from 'constants/countryData';
import RegionFields from 'components/common/RegionFields';
import PrivacyPolicy from 'components/common/PrivacyPolicy';
import { IOption } from 'types/IOption';
import { IUserResource } from '../../../../server/types';

export type UserFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  phone: string;
  country: string;
  state: string;
  zipCode: string;
  password: string;
  confirmPassword: string;
  status?: AccountStatus;
  recaptcha?: string;
  role?: string;
  roles?: string[];
};

type UserFormProps = {
  onSubmit: (values: UserFormValues, actions: FormikHelpers<UserFormValues>) => void;
  initialValues?: UserFormValues;
  error?: string;
  data?: Partial<IUserResource>;
  buttonText?: string;
  changePassword?: boolean;
  admin?: boolean;
  roles?: IOption[];
  buttonProps?: ButtonProps;
  buttonContainerProps?: BoxProps;
};

const UserForm = ({
  onSubmit,
  initialValues = {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    country: 'CA',
    phone: '',
    state: '',
    zipCode: '',
    company: '',
  },
  error,
  data,
  buttonText = 'REGISTER',
  changePassword = false,
  admin = false,
  buttonProps = {},
  buttonContainerProps = {},
  roles = [],
}: UserFormProps) => {
  const [submitted, setSubmitted] = useState(false);
  const [cachedEmail, setCachedEmail] = useVariable<string>();

  const [checkEmail] = useValidateEmail();
  const [showToast] = useToast();

  const [fetchCountries, { loading: fetchCountriesLoading, error: fetchCountriesError, data: countriesData }] =
    useFetchCountries();

  const [
    fetchCountryStates,
    { loading: fetchCountryStatesLoading, data: countryStatesData, error: countryStatesError },
  ] = useFetchCountryStates();

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    if (!data?.country) return;

    fetchCountryStates(data.country);
  }, [data]);

  useEffect(() => {
    if (!countryStatesError) return;

    showToast({
      message: countryStatesError,
      status: 'error',
    });
  }, [countryStatesError]);

  const renderError = error ? (
    <Flex justifyContent="center" p={4}>
      <Alert
        flexDirection="column"
        alignItems="center"
        status="error"
        justifyContent="center"
        bgColor="transparent"
        mb={4}
      >
        <AlertIcon mb={2} boxSize="40px" mr={0} color="primaryThemeColour.400" />
        <AlertTitle color="black">The following errors occurred:</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    </Flex>
  ) : null;

  const validateCheckEmail = async (userData?: IUserResource, email?: string) => {
    if (!email) return null;

    if (!userData && !validateEmail(email)) {
      return errorMessageEmailInvalid;
    }
    if (cachedEmail !== email) {
      const emailCheck = await checkEmail(email);
      if (typeof emailCheck !== 'string') {
        return emailCheck?.data?.message;
      }
    }

    setCachedEmail(email);

    return null;
  };

  const validatePasswordStrength = (password?: string) => {
    if (!password) return null;

    const score = validatePassword(password);

    if (!score) {
      return errorMessagePasswordShort;
    }

    if (score && score < 3) {
      return errorMessagePasswordStrength;
    }

    return null;
  };

  if (checkIfError(countriesData))
    return (
      <Alert status="error" w="auto" borderRadius={4} variant="solid">
        <AlertIcon />
        <AlertDescription maxWidth="sm">{fetchCountriesError}</AlertDescription>
      </Alert>
    );

  const renderForm =
    fetchCountriesLoading || !countriesData ? (
      <Flex justifyContent="center" alignItems="center">
        <Spinner size="lg" />
      </Flex>
    ) : (
      <ThemeContext.Consumer>
        {(theme: ISettings | null) => (
          <Formik
            initialValues={(data as UserFormValues) ?? initialValues ?? {}}
            validate={async (values) => {
              const passwordRequiredFields = changePassword ? [] : ['password', 'confirmPassword'];

              const adminRequiredFields = admin ? ['status', 'role'] : [];

              const countryRequiredFields = getCountryRequiredFields(values.country);

              const errors = validateRequired<UserFormValues>(
                [
                  'firstName',
                  'lastName',
                  'email',
                  'company',
                  'phone',
                  'country',
                  ...countryRequiredFields,
                  ...passwordRequiredFields,
                  ...adminRequiredFields,
                ],
                values
              );

              if (changePassword && values.password && !values.confirmPassword) {
                errors.confirmPassword = errorMessageRequired;
              }

              if (!data) {
                const emailError = await validateCheckEmail(data, values.email);

                if (emailError) errors.email = emailError;
              }

              if (!values.recaptcha) {
                errors.recaptcha = errorMessageRecaptchaFailed;
              }

              const passwordError = validatePasswordStrength(values.password);

              if (passwordError) {
                errors.password = passwordError;
              }

              if (
                values.password &&
                values.confirmPassword &&
                !validateConfirm(values.password, values.confirmPassword)
              ) {
                errors.confirmPassword = errorMessageConfirmPassword;
              }

              if (
                values.phone &&
                values.phone.replace(/\D/g, '').length > 0 &&
                values.phone.split('x')[0].replace(/\D/g, '').length < 10
              ) {
                errors.phone = errorMessagePhoneNumberInvalid;
              }

              if (values.zipCode && !validatePostalCode(values.country, values.zipCode)) {
                errors.zipCode = errorMessageZipCode;
              }

              return errors;
            }}
            onSubmit={onSubmit}
          >
            {(formConfig) => {
              const {
                values,
                errors,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                setFieldValue,
                setFieldTouched,
              } = formConfig;

              const selectProps: Omit<FormSelectProps, 'name'> = {
                filterConfig: {
                  matchFrom: 'start',
                },
                setFieldValue,
                setFieldTouched,
              };

              const otherCountryArr =
                theme?.otherCountry === 'true' ? [{ name: otherCountry.label, iso2: otherCountry.value }] : [];

              const passwordFieldTextAsterisk = !changePassword ? '*' : '';

              const possessiveAdj = admin ? 'the' : 'your';

              const formErrors = validateForm<UserFormValues>({
                ...formConfig,
                submitted,
              });

              const recaptchaError = submitted ? errors.recaptcha : undefined;

              const passwordFields = (
                <>
                  <Box>
                    <FormControl isInvalid={!!formErrors.password}>
                      <FormLabel htmlFor="password">PASSWORD {passwordFieldTextAsterisk}</FormLabel>
                      <Input
                        type="password"
                        name="password"
                        maxLength={20}
                        value={values.password}
                        error={formErrors.password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={`Enter ${possessiveAdj} password`}
                        isPasswordStrength
                        isTogglePassword
                        autoComplete="off"
                      />
                    </FormControl>
                  </Box>
                  <Box>
                    <FormControl isInvalid={!!formErrors.confirmPassword}>
                      <FormLabel htmlFor="password">CONFIRM PASSWORD {passwordFieldTextAsterisk}</FormLabel>
                      <Input
                        type="password"
                        name="confirmPassword"
                        maxLength={20}
                        value={values.confirmPassword}
                        error={formErrors.confirmPassword}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={`Confirm ${possessiveAdj} password`}
                        isTogglePassword={false}
                      />
                    </FormControl>
                  </Box>
                </>
              );

              const renderAdminFields = admin ? (
                <>
                  <Box>
                    <FormControl isInvalid={!!formErrors.role}>
                      <FormLabel htmlFor="role">ROLE *</FormLabel>
                      <FormSelect
                        name="role"
                        error={formErrors.role}
                        value={values.role}
                        placeholder={`Select ${possessiveAdj} role`}
                        options={roles}
                        {...selectProps}
                      />
                    </FormControl>
                  </Box>
                  <Box>
                    <FormControl isInvalid={!!formErrors.status}>
                      <FormLabel htmlFor="status">ACCOUNT STATUS *</FormLabel>
                      <FormSelect
                        name="status"
                        error={formErrors.status}
                        value={values.status}
                        placeholder={`Select ${possessiveAdj} account status`}
                        options={accountStatuses}
                        {...selectProps}
                      />
                    </FormControl>
                  </Box>
                </>
              ) : null;

              const renderPasswordFields = !changePassword ? passwordFields : null;

              const renderChangePasswordFields = changePassword ? (
                <Box mb={6}>
                  <Heading as="h4" size="lg" mt={4} mb={6} fontWeight="normal">
                    Change Password
                  </Heading>
                  <SimpleGrid columns={2} spacing={4}>
                    {passwordFields}
                  </SimpleGrid>
                </Box>
              ) : null;

              return (
                <form onSubmit={handleSubmit}>
                  {renderError}
                  <SimpleGrid columns={2} spacing={4} mb={6}>
                    <Box>
                      <FormControl isInvalid={!!formErrors.email}>
                        <FormLabel htmlFor="email">EMAIL *</FormLabel>
                        <Input
                          type="email"
                          name="email"
                          maxLength={100}
                          value={values.email}
                          error={formErrors.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isDisabled={!!data?.id}
                          placeholder={`Enter ${possessiveAdj} email`}
                          autoComplete="off"
                        />
                      </FormControl>
                    </Box>
                    <Box>
                      <FormControl isInvalid={!!formErrors.company}>
                        <FormLabel htmlFor="company">COMPANY *</FormLabel>
                        <Input
                          name="company"
                          maxLength={100}
                          error={formErrors.company}
                          value={values.company}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder={`Enter ${possessiveAdj} company`}
                        />
                      </FormControl>
                    </Box>
                    <Box>
                      <FormControl isInvalid={!!formErrors.firstName}>
                        <FormLabel htmlFor="firstName">FIRST NAME *</FormLabel>
                        <Input
                          name="firstName"
                          maxLength={100}
                          value={values.firstName}
                          error={formErrors.firstName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder={`Enter ${possessiveAdj} first name`}
                        />
                      </FormControl>
                    </Box>
                    <Box>
                      <FormControl isInvalid={!!formErrors.lastName}>
                        <FormLabel htmlFor="lastName">LAST NAME *</FormLabel>
                        <Input
                          name="lastName"
                          maxLength={100}
                          value={values.lastName}
                          error={formErrors.lastName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder={`Enter ${possessiveAdj} last name`}
                        />
                      </FormControl>
                    </Box>
                    {renderAdminFields}
                    {renderPasswordFields}

                    <Box>
                      <FormControl isInvalid={!!formErrors.phone}>
                        <FormLabel htmlFor="phone">PHONE *</FormLabel>
                        <Input
                          name="phone"
                          mask="(999) 999-9999 x 9999"
                          error={formErrors.phone}
                          placeholder="(999) 999-9999"
                          value={values.phone}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </FormControl>
                    </Box>

                    <RegionFields
                      {...formConfig}
                      countryStatesLoading={!!fetchCountryStatesLoading}
                      values={values}
                      errors={formErrors}
                      possessiveAdj={possessiveAdj}
                      countries={[
                        ...(countriesData ?? [])
                          .filter(({ iso2 }) => theme?.countries.includes(iso2) || data?.country === iso2)
                          .sort((a, b) =>
                            !theme?.countries ? 1 : theme.countries.indexOf(a.iso2) - theme.countries.indexOf(b.iso2)
                          ),
                        ...otherCountryArr,
                      ]}
                      countryStates={!checkIfError(countryStatesData) ? countryStatesData ?? [] : []}
                      onChangeCountry={(country: string) => fetchCountryStates(country)}
                    />
                  </SimpleGrid>
                  {renderChangePasswordFields}

                  <FormControl isInvalid={!!recaptchaError}>
                    <Recaptcha onChange={(token: string) => setFieldValue('recaptcha', token)} error={recaptchaError} />
                  </FormControl>
                  <PrivacyPolicy policy={theme?.privacyPolicy} />
                  <Flex justifyContent="flex-end" {...buttonContainerProps}>
                    <Button
                      isDisabled={isSubmitting}
                      isLoading={isSubmitting}
                      onClick={() => setSubmitted(true)}
                      type="submit"
                      {...buttonProps}
                    >
                      {buttonText}
                    </Button>
                  </Flex>
                </form>
              );
            }}
          </Formik>
        )}
      </ThemeContext.Consumer>
    );

  return renderForm;
};

export default UserForm;
